/**
 * useUnifiedInbox Hook
 *
 * Combines platform messages (from Tenant API) with application messages (from Data Plane)
 * into a unified inbox view with filtering and sorting capabilities.
 */

import { useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { useInbox, INBOX_QUERY_KEY, INBOX_UNREAD_COUNT_QUERY_KEY } from './useInbox';
import { useAppMessages, APP_MESSAGES_QUERY_KEY, APP_MESSAGES_UNREAD_COUNT_QUERY_KEY } from './useAppMessages';
import { useAllAppMessages, ALL_APP_MESSAGES_QUERY_KEY, ALL_APP_MESSAGES_UNREAD_COUNT_QUERY_KEY } from './useAllAppMessages';
import { inboxService } from '../../../api/inbox.service';
import { appMessageService } from '../../../api/app-message.service';
import type {
  UnifiedInboxItem,
  InboxSourceFilter,
  AppMessageView,
  MessagePriority,
} from '../../../types/app-message.types';
import type { InboxMessageSanitized, InboxView, BulkActionType, BulkActionResult } from '../../../types/inbox.types';
import type { AppMessageSanitized } from '../../../types/app-message.types';

// ============================================================================
// HOOK OPTIONS
// ============================================================================

/** Common view type for unified inbox */
export type UnifiedInboxView = 'inbox' | 'archived' | 'deleted';

export interface UseUnifiedInboxOptions {
  /** Filter by source (ALL, PLATFORM, APPLICATION) */
  sourceFilter?: InboxSourceFilter;
  /** Filter by specific application (when sourceFilter is APPLICATION) */
  applicationId?: string;
  /** Show only unread messages */
  unreadOnly?: boolean;
  /** View type (inbox, archived, deleted) - defaults to inbox */
  view?: UnifiedInboxView;
  /** Filter by message type */
  messageType?: string;
  /** Filter by priority */
  priority?: MessagePriority;
  /** Filter by date range */
  dateRange?: {
    start?: string;
    end?: string;
  };
}

// ============================================================================
// HOOK RESULT
// ============================================================================

export interface UseUnifiedInboxResult {
  /** Combined and sorted inbox items */
  items: UnifiedInboxItem[];
  /** Total unread count (platform + app) */
  totalUnreadCount: number;
  /** Platform messages unread count */
  platformUnreadCount: number;
  /** App messages unread count */
  appUnreadCount: number;
  /** Loading state (true if either source is loading) */
  isLoading: boolean;
  /** Error message (from either source) */
  error: string | null;
  /** Has more items to load */
  hasMore: boolean;
  /** Refresh all sources */
  refresh: () => void;
  /** Mark item as read (dispatches to appropriate service) */
  markAsRead: (item: UnifiedInboxItem) => Promise<void>;
  /** Mark all items as read in both sources */
  markAllAsRead: () => Promise<void>;
  /** Archive item (dispatches to appropriate service) */
  archiveMessage: (item: UnifiedInboxItem) => Promise<void>;
  /** Unarchive item (dispatches to appropriate service) */
  unarchiveMessage: (item: UnifiedInboxItem) => Promise<void>;
  /** Soft delete item (dispatches to appropriate service) */
  softDeleteMessage: (item: UnifiedInboxItem) => Promise<void>;
  /** Restore deleted item (dispatches to appropriate service) */
  restoreMessage: (item: UnifiedInboxItem) => Promise<void>;
  /** Permanently delete item (dispatches to appropriate service) */
  permanentDeleteMessage: (item: UnifiedInboxItem) => Promise<void>;
  /** Perform a bulk action on multiple items (splits by source automatically) */
  bulkAction: (action: BulkActionType, items: UnifiedInboxItem[]) => Promise<BulkActionResult>;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convert platform inbox message to unified format
 */
function platformMessageToUnified(msg: InboxMessageSanitized): UnifiedInboxItem {
  let actionUrl: string | undefined;

  // Generate action URL based on related entity
  if (msg.relatedEntity) {
    switch (msg.relatedEntity.type) {
      case 'SUPPORT_TICKET':
        actionUrl = `/app/support?ticket=${msg.relatedEntity.id}`;
        break;
      case 'ANNOUNCEMENT':
        actionUrl = `/announcements/${msg.relatedEntity.id}`;
        break;
    }
  }

  return {
    source: 'platform',
    id: msg.messageId,
    subject: msg.subject,
    body: msg.body,
    priority: msg.priority,
    isRead: msg.isRead,
    isArchived: msg.isArchived,
    isDeleted: msg.isDeleted,
    deletedAt: msg.deletedAt,
    createdAt: msg.createdAt,
    sender: {
      type: msg.sender.type,
      name: msg.sender.name,
    },
    actionUrl,
  };
}

/**
 * Convert app message to unified format
 */
function appMessageToUnified(msg: AppMessageSanitized): UnifiedInboxItem {
  return {
    source: 'application',
    id: msg.messageId,
    subject: msg.subject,
    body: msg.body,
    priority: msg.priority,
    isRead: msg.isRead,
    isArchived: msg.isArchived,
    isDeleted: msg.isDeleted,
    deletedAt: msg.deletedAt,
    createdAt: msg.createdAt,
    sender: {
      type: 'APPLICATION',
      name: msg.applicationName,
      applicationCode: msg.applicationCode,
    },
    actionUrl: msg.actionUrl,
    data: msg.data,
    applicationId: msg.applicationId,
  };
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Hook for unified inbox combining platform and app messages
 */
export function useUnifiedInbox(options?: UseUnifiedInboxOptions): UseUnifiedInboxResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const sourceFilter = options?.sourceFilter || 'all';
  const view = options?.view || 'inbox';

  // ============================================================================
  // FETCH PLATFORM MESSAGES
  // ============================================================================

  const platformInbox = useInbox({
    view: view as InboxView,
    unreadOnly: options?.unreadOnly,
    type: options?.messageType as any, // Platform message type
    priority: options?.priority as any,
    dateStart: options?.dateRange?.start,
    dateEnd: options?.dateRange?.end,
  });

  // ============================================================================
  // FETCH APP MESSAGES
  // ============================================================================

  // Determine whether to use application-specific or cross-application queries
  const hasSpecificApp = !!options?.applicationId;
  const shouldFetchAppMessages = sourceFilter === 'all' || sourceFilter === 'applications';

  // Use application-specific hook when filtering by a specific app
  const singleAppMessages = useAppMessages({
    applicationId: options?.applicationId,
    view: view as AppMessageView,
    unreadOnly: options?.unreadOnly,
    type: options?.messageType,
    priority: options?.priority,
    dateStart: options?.dateRange?.start,
    dateEnd: options?.dateRange?.end,
    enabled: shouldFetchAppMessages && hasSpecificApp,
  });

  // Use cross-application hook when showing all applications
  const allAppMessages = useAllAppMessages({
    view: view as AppMessageView,
    unreadOnly: options?.unreadOnly,
    type: options?.messageType,
    priority: options?.priority,
    dateStart: options?.dateRange?.start,
    dateEnd: options?.dateRange?.end,
    enabled: shouldFetchAppMessages && !hasSpecificApp,
  });

  // Select the appropriate app messages source
  const appMessages = hasSpecificApp ? singleAppMessages : allAppMessages;

  // ============================================================================
  // COMBINE AND SORT MESSAGES
  // ============================================================================

  const unifiedItems = useMemo(() => {
    const combined: UnifiedInboxItem[] = [];

    // Add platform messages if filter allows
    if (sourceFilter === 'all' || sourceFilter === 'platform') {
      combined.push(...platformInbox.messages.map(platformMessageToUnified));
    }

    // Add app messages if filter allows
    if (sourceFilter === 'all' || sourceFilter === 'applications') {
      combined.push(...appMessages.messages.map(appMessageToUnified));
    }

    // Sort by createdAt descending (newest first)
    return combined.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [platformInbox.messages, appMessages.messages, sourceFilter]);

  // ============================================================================
  // COMBINED METRICS
  // ============================================================================

  const totalUnreadCount = useMemo(() => {
    if (sourceFilter === 'platform') {
      return platformInbox.unreadCount;
    }
    if (sourceFilter === 'applications') {
      return appMessages.unreadCount;
    }
    return platformInbox.unreadCount + appMessages.unreadCount;
  }, [platformInbox.unreadCount, appMessages.unreadCount, sourceFilter]);

  const isLoading = platformInbox.isLoading || appMessages.isLoading;

  const error = platformInbox.error || appMessages.error;

  const hasMore = platformInbox.hasMore || appMessages.hasMore;

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Refresh all sources
   */
  const refresh = useCallback(() => {
    platformInbox.refresh();
    if (hasSpecificApp) {
      singleAppMessages.refresh();
    } else {
      allAppMessages.refresh();
    }
  }, [platformInbox, singleAppMessages, allAppMessages, hasSpecificApp]);

  /**
   * Mark item as read (dispatch to appropriate service)
   */
  const markAsRead = useCallback(
    async (item: UnifiedInboxItem) => {
      if (item.source === 'platform') {
        await platformInbox.markAsRead(item.id);
      } else if (hasSpecificApp) {
        // Single app mode - just pass messageId
        await singleAppMessages.markAsRead(item.id);
      } else {
        // Cross-app mode - pass both messageId and applicationId
        await allAppMessages.markAsRead(item.id, item.applicationId!);
      }
    },
    [platformInbox, singleAppMessages, allAppMessages, hasSpecificApp]
  );

  /**
   * Mark all items as read in both sources
   * Note: Cross-app mode doesn't support markAllAsRead, so we skip app messages in that case
   */
  const markAllAsRead = useCallback(async () => {
    const promises: Promise<void>[] = [];

    if (sourceFilter === 'all' || sourceFilter === 'platform') {
      promises.push(platformInbox.markAllAsRead());
    }

    // markAllAsRead is only supported in single-app mode
    if ((sourceFilter === 'all' || sourceFilter === 'applications') && hasSpecificApp) {
      promises.push(singleAppMessages.markAllAsRead());
    }

    await Promise.all(promises);
  }, [platformInbox, singleAppMessages, sourceFilter, hasSpecificApp]);

  /**
   * Archive item (dispatch to appropriate service)
   */
  const archiveMessage = useCallback(
    async (item: UnifiedInboxItem) => {
      if (item.source === 'platform') {
        await platformInbox.archiveMessage(item.id);
      } else if (hasSpecificApp) {
        await singleAppMessages.archiveMessage(item.id);
      } else {
        await allAppMessages.archiveMessage(item.id, item.applicationId!);
      }
    },
    [platformInbox, singleAppMessages, allAppMessages, hasSpecificApp]
  );

  /**
   * Unarchive item (dispatch to appropriate service)
   */
  const unarchiveMessage = useCallback(
    async (item: UnifiedInboxItem) => {
      if (item.source === 'platform') {
        await platformInbox.unarchiveMessage(item.id);
      } else if (hasSpecificApp) {
        await singleAppMessages.unarchiveMessage(item.id);
      } else {
        await allAppMessages.unarchiveMessage(item.id, item.applicationId!);
      }
    },
    [platformInbox, singleAppMessages, allAppMessages, hasSpecificApp]
  );

  /**
   * Soft delete item (dispatch to appropriate service)
   */
  const softDeleteMessage = useCallback(
    async (item: UnifiedInboxItem) => {
      if (item.source === 'platform') {
        await platformInbox.softDeleteMessage(item.id);
      } else if (hasSpecificApp) {
        await singleAppMessages.softDeleteMessage(item.id);
      } else {
        await allAppMessages.softDeleteMessage(item.id, item.applicationId!);
      }
    },
    [platformInbox, singleAppMessages, allAppMessages, hasSpecificApp]
  );

  /**
   * Restore deleted item (dispatch to appropriate service)
   */
  const restoreMessage = useCallback(
    async (item: UnifiedInboxItem) => {
      if (item.source === 'platform') {
        await platformInbox.restoreMessage(item.id);
      } else if (hasSpecificApp) {
        await singleAppMessages.restoreMessage(item.id);
      } else {
        await allAppMessages.restoreMessage(item.id, item.applicationId!);
      }
    },
    [platformInbox, singleAppMessages, allAppMessages, hasSpecificApp]
  );

  /**
   * Permanently delete item (dispatch to appropriate service)
   */
  const permanentDeleteMessage = useCallback(
    async (item: UnifiedInboxItem) => {
      if (item.source === 'platform') {
        await platformInbox.permanentDeleteMessage(item.id);
      } else if (hasSpecificApp) {
        await singleAppMessages.permanentDeleteMessage(item.id);
      } else {
        await allAppMessages.permanentDeleteMessage(item.id, item.applicationId!);
      }
    },
    [platformInbox, singleAppMessages, allAppMessages, hasSpecificApp]
  );

  // ============================================================================
  // BULK ACTION
  // ============================================================================

  const bulkAction = useCallback(
    async (action: BulkActionType, selectedItems: UnifiedInboxItem[]): Promise<BulkActionResult> => {
      const tenantId = user?.tenantId || '';
      const userId = user?.userId || '';
      if (!tenantId || !userId) {
        return { successCount: 0, failedIds: selectedItems.map((i) => i.id) };
      }

      // 1. Split by source
      const platformItems = selectedItems.filter((i) => i.source === 'platform');
      const appItems = selectedItems.filter((i) => i.source === 'application');

      // 2. Build API call promises
      const promises: Promise<BulkActionResult>[] = [];

      if (platformItems.length > 0) {
        promises.push(
          inboxService.bulkAction(tenantId, userId, action, platformItems.map((i) => i.id))
        );
      }

      if (appItems.length > 0) {
        if (hasSpecificApp && options?.applicationId) {
          promises.push(
            appMessageService.bulkAction(tenantId, options.applicationId, userId, action, appItems.map((i) => i.id))
          );
        } else {
          promises.push(
            appMessageService.bulkActionCrossApp(tenantId, userId, action,
              appItems.map((i) => ({ messageId: i.id, applicationId: i.applicationId! }))
            )
          );
        }
      }

      // 3. Fire in parallel
      const results = await Promise.allSettled(promises);

      // 4. Aggregate results
      let totalSuccess = 0;
      let totalFailed: string[] = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          totalSuccess += result.value.successCount;
          totalFailed = totalFailed.concat(result.value.failedIds);
        }
      });
      // For rejected promises, count all items from that batch as failed
      if (results[0]?.status === 'rejected' && platformItems.length > 0) {
        totalFailed = totalFailed.concat(platformItems.map((i) => i.id));
      }
      if (results.length > 1 && results[results.length - 1]?.status === 'rejected' && appItems.length > 0) {
        totalFailed = totalFailed.concat(appItems.map((i) => i.id));
      } else if (results.length === 1 && platformItems.length === 0 && results[0]?.status === 'rejected') {
        totalFailed = totalFailed.concat(appItems.map((i) => i.id));
      }

      // 5. Invalidate React Query caches
      if (platformItems.length > 0) {
        queryClient.invalidateQueries({ queryKey: [INBOX_QUERY_KEY, tenantId, userId] });
        queryClient.invalidateQueries({ queryKey: [INBOX_UNREAD_COUNT_QUERY_KEY, tenantId, userId] });
      }
      if (appItems.length > 0) {
        if (hasSpecificApp && options?.applicationId) {
          queryClient.invalidateQueries({ queryKey: [APP_MESSAGES_QUERY_KEY, tenantId, options.applicationId, userId] });
          queryClient.invalidateQueries({ queryKey: [APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, options.applicationId, userId] });
        } else {
          queryClient.invalidateQueries({ queryKey: [ALL_APP_MESSAGES_QUERY_KEY, tenantId, userId] });
          queryClient.invalidateQueries({ queryKey: [ALL_APP_MESSAGES_UNREAD_COUNT_QUERY_KEY, tenantId, userId] });
        }
      }

      return { successCount: totalSuccess, failedIds: totalFailed };
    },
    [user, hasSpecificApp, options?.applicationId, queryClient]
  );

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    items: unifiedItems,
    totalUnreadCount,
    platformUnreadCount: platformInbox.unreadCount,
    appUnreadCount: appMessages.unreadCount,
    isLoading,
    error,
    hasMore,
    refresh,
    markAsRead,
    markAllAsRead,
    archiveMessage,
    unarchiveMessage,
    softDeleteMessage,
    restoreMessage,
    permanentDeleteMessage,
    bulkAction,
  };
}
